import axios from "../../axiosInstance";

// routes/dropdown is paginated; one page this size holds all of an
// account's routes.
const DROPDOWN_PER_PAGE = 500;

const ENDPOINTS = {
  list: (page) => `routes/get?page=${page}`,
  create: () => `routes/create`,
  update: (id) => `routes/edit/${id}`,
  remove: (id) => `routes/delete/${id}`,
  dropdown: () => `routes/dropdown?perPage=${DROPDOWN_PER_PAGE}`,
  orderRoute: (orderId) => `routes/update-order-route/${orderId}`,
};

const toPoint = ({ lat, lng }) => ({
  lat: Number(lat.toFixed(6)),
  lng: Number(lng.toFixed(6)),
});

const toApiRoute = ({ name, color, prefixes, paths }) => ({
  name: name.trim(),
  color,
  postal_prefixes: prefixes,
  coordinates: paths.map((path) => path.map(toPoint)),
});

const fromApiRoute = (raw) => ({
  id: raw?.id,
  name: raw?.name ?? "",
  color: raw?.color ?? "",
  prefixes: (raw?.postal_prefixes || [])
    .map((item) => item?.postal_prefix)
    .filter(Boolean)
    .map((prefix) => prefix.toUpperCase()),
});

const readPage = (body) => ({
  routes: body?.result?.data ?? [],
  lastPage: Number(body?.result?.last_page) || 1,
});
const readOne = (body) => body?.result;

export const fetchDeliveryRoutes = async () => {
  let response = { data: null, error: null };
  try {
    const first = readPage((await axios.get(ENDPOINTS.list(1))).data);
    const rest = await Promise.all(
      Array.from({ length: first.lastPage - 1 }, (_, i) =>
        axios.get(ENDPOINTS.list(i + 2)),
      ),
    );
    const routes = [
      ...first.routes,
      ...rest.flatMap((res) => readPage(res.data).routes),
    ];
    response = { ...response, data: routes.map(fromApiRoute) };
  } catch (error) {
    response = { ...response, error };
  }
  return response;
};

export const createDeliveryRoute = async (route) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(ENDPOINTS.create(), toApiRoute(route));
    response = { ...response, data: fromApiRoute(readOne(res.data)) };
  } catch (error) {
    response = { ...response, error };
  }
  return response;
};

export const updateDeliveryRoute = async (id, route) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(ENDPOINTS.update(id), toApiRoute(route));
    response = { ...response, data: fromApiRoute(readOne(res.data)) };
  } catch (error) {
    response = { ...response, error };
  }
  return response;
};

export const deleteDeliveryRoute = async (id) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.delete(ENDPOINTS.remove(id));
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error };
  }
  return response;
};

// Every route (id, name, color), for the route dropdown on the Orders pages.
export const fetchRoutesDropdown = async () => {
  let response = { data: null, error: null };
  try {
    const res = await axios.get(ENDPOINTS.dropdown());
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};

// Puts an order on a route, or takes it off its route when routeId is null.
export const updateOrderRoute = async (orderId, routeId) => {
  let response = { data: null, error: null };
  try {
    const res = await axios.post(ENDPOINTS.orderRoute(orderId), {
      route_id: routeId,
    });
    response = { ...response, data: res.data };
  } catch (error) {
    response = { ...response, error: error };
  }
  return response;
};
